import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "../Header";
import Field from "../../../../../Settings/Content/Components/Field";
import TrashBucketIcon from "../../../../../../Components/Icon-SVG/TrashBucketIcon";
import Tooltip from "../../../../../../Components/common/Tooltip/Tooltip";
import Backdrop from "../../../../../../Components/UI/Backdrop/Backdrop";

import { RequestUpdateAgentPolicy } from "../../../../../../store/actions/integrationsActions";
import { addNotification } from "../../../../../../store/actions/notifierActions";

const MIN_MAX_VALUES = {
  maxPathDepth: [1, 30],
  maxSubmissionSize: [19250000, 57750000],
  minSubmissionSize: [0, 10000000],
};

const SCALAR_FIELDS = ["maxPathDepth", "maxSubmissionSize", "minSubmissionSize"];
const CSV_FIELDS = ["fileExtensions", "excludedPaths"];

const toBool = (v) => v === true || v === "true" || v === "True";
const toArray = (v) =>
  typeof v === "string"
    ? v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : Array.isArray(v)
      ? v
      : [];

const SubmissionForm = ({ selectedHost }) => {
  const dispatch = useDispatch();

  const policy = useSelector((state) => state.integrations.agentPolicy);
  const updatePolicyLoading = useSelector((state) => state.integrations.updatePolicyLoading);

  const backend = policy?.settings?.submission;

  const [submissionUI, setSubmissionUI] = useState({
    ...backend,
    fileExtensions: toArray(backend?.fileExtensions),
    excludedPaths: toArray(backend?.excludedPaths),
  });
  const [buffers, setBuffers] = useState({ fileExtensions: "", excludedPaths: "" });

  const numberChangeHandler = (e) => {
    const { name, value } = e.target;
    setSubmissionUI((prev) => ({ ...prev, [name]: value === "" ? "" : Number(value) }));
  };

  const boolChangeHandler = (e) => {
    const { name, value } = e.target;
    setSubmissionUI((prev) => ({ ...prev, [name]: value === "true" }));
  };

  const blurHandler = (e) => {
    const { name, value } = e.target;
    const limits = MIN_MAX_VALUES[name];
    if (!limits) return;

    const [min, max] = limits;
    let newValue = Number(value);

    if (newValue < min) newValue = min;
    if (newValue > max) newValue = max;

    setSubmissionUI((prev) => ({ ...prev, [name]: newValue }));
  };

  const bufferChangeHandler = (name, value) => {
    setBuffers((prev) => ({ ...prev, [name]: value }));
  };

  const addCsvItem = (name) => {
    const item = (buffers[name] || "").trim();

    if (item === "") {
      dispatch(
        addNotification({
          message: "Enter a valid value",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    if ((submissionUI[name] || []).some((existing) => existing === item)) {
      dispatch(
        addNotification({
          message: "Value already exists",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }

    setSubmissionUI((prev) => ({ ...prev, [name]: [...(prev[name] || []), item] }));
    setBuffers((prev) => ({ ...prev, [name]: "" }));
  };

  const removeCsvItem = (name, index) => {
    setSubmissionUI((prev) => ({ ...prev, [name]: prev[name].filter((_, i) => i !== index) }));
  };

  const checkSaveButtonDisabled = () => {
    if (!backend || !submissionUI) return true;

    for (const f of SCALAR_FIELDS) {
      if (backend[f] !== submissionUI[f]) {
        return false;
      }
    }

    for (const f of CSV_FIELDS) {
      const backendArr = toArray(backend[f]).slice().sort();
      const uiArr = [...(submissionUI[f] || [])].sort();
      if (backendArr.length !== uiArr.length) return false;
      if (!backendArr.every((v, i) => v === uiArr[i])) return false;
    }

    return true;
  };

  const updateAgentSettingsHandler = (e) => {
    e.preventDefault();
    if (updatePolicyLoading) return;

    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          submission: {
            ...submissionUI,
            fileExtensions: submissionUI.fileExtensions.join(","),
            excludedPaths: submissionUI.excludedPaths.join(","),
          },
        },
        host: selectedHost,
      }),
    );
  };

  const toggleStatusHandler = () => {
    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          submission: {
            ...policy.settings.submission,
            enabled: !policy?.settings?.submission?.enabled,
          },
        },
        host: selectedHost,
      }),
    );
  };

  const renderCsvList = (name, label, tooltip, placeholder) => (
    <>
      <div className="agent-policy-form__group-line">
        <Field
          label={label}
          tooltipText={tooltip}
          width="300px"
          value={buffers[name]}
          onChange={(e) => bufferChangeHandler(name, e.target.value)}
          parentClassName="p-r-20"
          placeholder={placeholder}
        />
        <button
          type="button"
          className="products-body-form__button"
          onClick={() => addCsvItem(name)}>
          +
        </button>
      </div>

      <ul className="agent-policy-form__list" style={{ gridColumn: "span 2" }}>
        {submissionUI?.[name]?.length > 0 ? (
          submissionUI[name].map((item, index) => (
            <li key={index} className="agent-policy-form__list-item">
              {item}
              <button
                type="button"
                style={{ display: "flex" }}
                onClick={() => removeCsvItem(name, index)}>
                <TrashBucketIcon width="16" height="16" />
                <p style={{ marginLeft: "5px" }}>Delete</p>
              </button>
            </li>
          ))
        ) : (
          <li className="no-data">&nbsp;No Data</li>
        )}
      </ul>
    </>
  );

  return (
    <>
      <Header
        policy={policy}
        selectedHost={selectedHost}
        toggleProps={{
          show: true,
          text: "Submission Status",
          active: policy?.settings?.submission?.enabled,
          onClick: toggleStatusHandler,
        }}
      />
      <form className="agent-policy-form">
        <div className="agent-policy-form__line">
          <Field
            label="Max Submission Size in bytes"
            tooltipText="Min: 19250000, Max: 57750000"
            width="220px"
            name="maxSubmissionSize"
            value={submissionUI.maxSubmissionSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Min Submission Size in bytes"
            tooltipText="Min: 0, Max: 10000000"
            width="220px"
            name="minSubmissionSize"
            value={submissionUI.minSubmissionSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <div className="agent-policy-form__line">
          <Field
            label="Max Path Depth"
            tooltipText="Skip files deeper than this many folders. Min: 1, Max: 30"
            width="220px"
            name="maxPathDepth"
            value={submissionUI.maxPathDepth}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        {renderCsvList(
          "fileExtensions",
          "File Extension",
          "File types eligible for submission",
          "Enter an extension (e.g., exe, dll, pdf)",
        )}

        {renderCsvList(
          "excludedPaths",
          "Excluded Path",
          "Files whose full path contains any of these substrings are skipped (case-sensitive)",
          "e.g. C:\\Program Files\\, C:\\Windows\\Temp\\, \\AppData\\Local\\Temp\\",
        )}

        <button
          className="primary-button"
          disabled={checkSaveButtonDisabled()}
          onClick={updateAgentSettingsHandler}>
          Save
        </button>

        <Backdrop
          spinner={false}
          absolute
          show={!policy?.settings?.submission?.enabled}
          message="Submission settings are inactive! Please click on the status button at the header to activate submission settings."
          messageStyle={{ paddingTop: 0 }}
        />
      </form>
    </>
  );
};

export default SubmissionForm;
